import { useState, useEffect } from "react";
import { makeStyles, withStyles } from "@mui/styles";
import {
	Grid2 as Grid,
	Container,
	Typography,
	Box,
	TextField,
	Button,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Icon,
} from "@mui/material";
import { LargeActionButton } from "../../components/Common/LargeActionButton";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import { ModalWithActionButton } from "../Modal/ModalWithActionButton";
const useStyles = makeStyles((theme) => ({
	buttonContainer: {
		marginTop: "8px",
	},
	submitButton: {
		marginLeft: "2px",
	},
	cancelButton: {
		marginRight: "2px",
	},
	removeItemIcon: {
		cursor: "pointer",
	},
}));

const OperationButton = withStyles({
	root: {
		height: "56px",
		width: "56px",
		minWidth: "56px",
	},
	contained: {
		padding: 0,
	},
})(Button);

export default function AdhocCreditRequestSubmit(props) {
	const { poNumber, invNumber, scannedBarcode, initialCreditData, onSubmit, onCancel, reasons } = props;
	const emptyAlertState = { show: false, message: "" };
	const classes = useStyles();
	const [creditSku, setCreditSku] = useState(scannedBarcode ?? "");
	const [creditSkuQty, setCreditSkuQty] = useState(null);
	const [creditSkuReason, setCreditSkuReason] = useState("");
	const [initialData, setInitialData] = useState(initialCreditData);
	const [selectedSkuItem, setSelectedSkuItem] = useState(null);
	const [creditSkuItems, setCreditSkuItems] = useState([]);
	const [alertState, setAlertState] = useState(emptyAlertState);

	const handleSubmitClick = () => {
		if (onSubmit) {
			onSubmit(creditSkuItems);
		}
		resetCreditSkuState();
		setCreditSkuItems([]);
	};

	const handleCancelClick = () => {
		resetCreditSkuState();
		setCreditSkuItems([]);
		if (onCancel) onCancel();
	};

	const handleCreditSkuChange = (event) => {
		const skuValue = event.target.value;
		const skuItem = initialData.maxCreditQuantities.find((i) => i.sku === skuValue);
		setCreditSku(skuValue);
		changeSelectedSkuItem(skuItem);
	};

	const changeSelectedSkuItem = (skuItem) => {
		setCreditSkuQty(null);
		setCreditSkuReason("");
		if (skuItem) setCreditSku(skuItem?.sku);

		setSelectedSkuItem({ sku: skuItem?.sku, maxCreditQuantity: skuItem?.maxQuantity });
	};

	const handleCreditSkuQtyChange = (event) => {
		const qtyValue = event.target.value;
		const qty = parseInt(qtyValue);
		if (selectedSkuItem && qty < selectedSkuItem.maxCreditQuantity && qty > 0) setCreditSkuQty(qty);
	};

	const handleIncreaseQty = () => {
		let qty = creditSkuQty ?? 0;
		if (selectedSkuItem && qty < selectedSkuItem.maxCreditQuantity) {
			qty = creditSkuQty + 1;
			setCreditSkuQty(qty);
		}
	};

	const handleDecreaseQty = () => {
		let qty = creditSkuQty ?? 0;
		if (qty > 0) {
			qty = creditSkuQty - 1;
			setCreditSkuQty(qty);
		}
	};

	const handleCreditSkuReasonChange = (event) => {
		setCreditSkuReason(event.target.value);
	};

	const handleAddCreditSkuItem = () => {
		if (canAddCreditSkuItem()) {
			if (creditSkuItems.some((i) => i.sku === creditSku)) {
				setAlertState({
					show: true,
					message:
						"SKU is already added. To change its quantity or reason. Please remove it first and add it again with desired values.",
				});
				return;
			}
			const creditSkuItem = { sku: creditSku, quantity: creditSkuQty, reason: creditSkuReason };
			creditSkuItems.push(creditSkuItem);
			setCreditSkuItems([...creditSkuItems]);
			resetCreditSkuState();
		}
	};

	const handleRemoveCreditSkuItem = (sku) => {
		const filtered = creditSkuItems.filter((item) => item.sku !== sku);
		setCreditSkuItems([...filtered]);
	};

	const handleCloseAlert = () => {
		setAlertState(emptyAlertState);
	};

	const resetCreditSkuState = () => {
		setCreditSku("");
		setCreditSkuQty(null);
		setCreditSkuReason("");
	};

	const canAddCreditSkuItem = () => creditSku && creditSkuQty > 0 && creditSkuReason;

	const canSubmit = () => creditSkuItems?.length > 0;

	const canIncrease = () => creditSkuQty < selectedSkuItem?.maxCreditQuantity;

	const canDecrease = () => creditSkuQty > 0;

	useEffect(() => {
		if (!scannedBarcode || !initialData.maxCreditQuantities) return;
		const skuItem = initialData.maxCreditQuantities.find((i) => i.barcode === scannedBarcode);
		changeSelectedSkuItem(skuItem);
	}, [scannedBarcode, initialData.maxCreditQuantities]);

	useEffect(() => {
		setInitialData(initialCreditData);
	}, [initialCreditData]);

	return (
		<Container disableGutters maxWidth="md">
			<ModalWithActionButton
				alertType="warning"
				primaryButtonName="Ok"
				onPrimaryButtonClick={handleCloseAlert}
				isOpen={alertState.show}
				message={alertState.message}
			/>
			<Grid container direction="column" justifyContent="center" spacing={2}>
				<Grid size="grow">
					<Typography variant="h6" component="div">
						New credit request
					</Typography>
					<Typography variant="body1" component="div">
						Add product(s) to the Awaiting Supplier credit for <strong>PO#</strong> {poNumber}
						{invNumber && (
							<>
								<strong> INV# </strong>
								{invNumber}
							</>
						)}
					</Typography>
				</Grid>
				<Grid size="grow">
					<TextField
						id="sku-input"
						name="sku-input"
						label="Sku"
						value={creditSku}
						variant="outlined"
						fullWidth
						required
						onChange={handleCreditSkuChange}
					/>
					{selectedSkuItem?.maxCreditQuantity === 0 && <span>Cannot submit credit request for this product.</span>}
					{selectedSkuItem?.maxCreditQuantity > 0 && (
						<span>Max quantity allowed: {selectedSkuItem?.maxCreditQuantity}</span>
					)}
				</Grid>
				<Grid size="grow">
					<Box display="flex" flexDirection="row" flexGrow={1}>
						<Box display="flex" justifyContent="flex-start">
							<OperationButton
								fullWidth
								variant="contained"
								color="secondary"
								onClick={handleDecreaseQty}
								disabled={!canDecrease()}
							>
								<RemoveIcon />
							</OperationButton>
						</Box>
						<Box flexGrow={2} mr="12px" ml="12px">
							<TextField
								type="number"
								id="qty-input"
								label="Quantity"
								variant="outlined"
								slotProps={{ htmlInput: { min: 0 } }}
								value={creditSkuQty ?? ""}
								fullWidth
								required
								onChange={handleCreditSkuQtyChange}
							/>
						</Box>
						<Box display="flex" justifyContent="flex-end">
							<OperationButton
								fullWidth
								variant="contained"
								color="secondary"
								onClick={handleIncreaseQty}
								disabled={!canIncrease()}
							>
								<AddIcon />
							</OperationButton>
						</Box>
					</Box>
				</Grid>
				<Grid size="grow">
					<FormControl variant="outlined" fullWidth>
						<InputLabel id="reason-label">Reason</InputLabel>
						<Select
							label="Reason"
							labelId="reason-label"
							id="reason-input"
							value={creditSkuReason}
							onChange={handleCreditSkuReasonChange}
							required
						>
							{reasons.map(
								(reason) =>
									reason.type === "Create" && (
										<MenuItem key={reason.value} value={reason.value}>
											{reason.value}
										</MenuItem>
									)
							)}
						</Select>
					</FormControl>
				</Grid>
				<Grid size="grow">
					<Box display="flex" justifyContent="end">
						<Button
							variant="contained"
							color="secondary"
							disabled={!canAddCreditSkuItem()}
							onClick={handleAddCreditSkuItem}
						>
							<AddIcon /> Add
						</Button>
					</Box>
				</Grid>
				{creditSkuItems?.length > 0 && (
					<Grid size="grow">
						<Grid container size={12}>
							<Grid size={4}>
								<Typography variant="caption" color="textSecondary" display="inline">
									SKU
								</Typography>
							</Grid>
							<Grid size={3}>
								<Typography variant="caption" color="textSecondary" display="inline">
									Qty
								</Typography>
							</Grid>
							<Grid size={4}>
								<Typography variant="caption" color="textSecondary" display="inline">
									Reason
								</Typography>
							</Grid>
							<Grid size={1}></Grid>
						</Grid>
						{creditSkuItems.map((skuItem) => {
							return (
								<Grid key={skuItem.sku} container size={12}>
									<Grid size={4}>
										<Typography variant="body2" color="textSecondary" display="inline">
											{skuItem.sku}
										</Typography>
									</Grid>
									<Grid size={3}>
										<Typography variant="body2" color="textSecondary" display="inline">
											{skuItem.quantity}
										</Typography>
									</Grid>
									<Grid size={4}>
										<Typography variant="body2" color="textSecondary" display="inline">
											{skuItem.reason}
										</Typography>
									</Grid>
									<Grid size={1}>
										<Icon
											color="primary"
											fontSize="small"
											className={classes.removeItemIcon}
											onClick={() => handleRemoveCreditSkuItem(skuItem.sku)}
										>
											<ClearIcon />
										</Icon>
									</Grid>
								</Grid>
							);
						})}
					</Grid>
				)}
			</Grid>
			<div className={classes.buttonContainer}>
				<Box display="flex" sx={{ gap: 2 }}>
					<LargeActionButton className={classes.cancelButton} onClick={handleCancelClick}>
						Back
					</LargeActionButton>

					<LargeActionButton className={classes.submitButton} onClick={handleSubmitClick} disabled={!canSubmit()}>
						Submit
					</LargeActionButton>
				</Box>
			</div>
		</Container>
	);
}
